import React, { useEffect, useState } from "react";
import {
  getSingleSurvey,
  addFormContent,
  updateFormContent,
  getFormContent,
  surveyResultPreview,
  imageUpload,
} from "../../../api/services/services";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Snackbar, Typography, Table, Box, CircularProgress, Grid, Paper, TableContainer, TableHead, TableBody, TableCell, TableRow, IconButton } from "@mui/material";
import { Bucket } from "../../../api/config/apiConfig";
import Input from "../../atoms/Input";
import ContactDetailField from "../../molecules/ContactDetailField";
import LoadingModal from "../../molecules/LoadingModal";
import MySignatureCanvas from "../../molecules/signature_canvas";
import SuperAdminVerifationModal from "../../molecules/SuperAdminVerifationModal";
import { sendSuperAdminVerificationForEdit } from "../PrebidAgreement/creation/service";
import PremiumCard from "../../molecules/PremiumCard";
import SectionHeader from "../../molecules/SectionHeader";
import PremiumPageLayout from "../../templates/PremiumPageLayout";
import PrintComponent from "../PrintComponent";
import WeightTicketDownload from "./download";
import moment from "moment";

import DescriptionIcon from "@mui/icons-material/Description";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import MonitorWeightIcon from "@mui/icons-material/MonitorWeight";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import GavelIcon from "@mui/icons-material/Gavel";
import trashIcn from "../../../imgs/trash.png";

const WeightTicketFormPage = () => {
  const { surveyId, id, flg } = useParams();
  const [surveyData, setSurveyData] = useState(null);
  const navigate = useNavigate();

  const [formContentId, setFormContentId] = useState("");
  const [isEdit, setIsEdit] = useState(flg === "1");
  const [isLoading, setIsLoading] = useState(false);

  const [formContent, setFormContent] = useState({
    dataRows: [
      {
        noOfPieces: "",
        grossWeight: 0,
        netWeight: 0,
        volWeight: 0,
        length: 0,
        width: 0,
        height: 0,
        cft: 0,
        cbm: 0,
      },
    ],
    customerName: "",
    customerEmail: "",
    refNo: "",
    date: "",
    originAddress: "",
    destination: "",
    adminSign: "",
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [mediaKeysToSend, setMediaKeysToSend] = useState([]);
  const [mediaKeysToRemove, setMediaKeysToRemove] = useState([]);
  const [open, setOpen] = useState(false);
  const [isSignCanvas, setSignCanvas] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const handleAddNewDataRows = () => {
    setFormContent((prev) => ({
      ...prev,
      dataRows: [...prev.dataRows, { noOfPieces: "", grossWeight: 0, netWeight: 0, volWeight: 0, length: 0, width: 0, height: 0, cft: 0, cbm: 0 }]
    }));
  };

  const handleRemoveDataRows = (ind) => {
    setFormContent((prev) => {
      const newRows = [...prev.dataRows];
      newRows.splice(ind, 1);
      return { ...prev, dataRows: newRows };
    });
  };

  const getSurveyPreview = () => {
    surveyResultPreview(surveyId).then((response) => {
      const detail = response?.data?.data?.surveyDetail;
      setSurveyData(detail);
      setFormContent((prev) => ({
        ...prev,
        customerName: detail?.name,
        customerEmail: detail?.email,
        refNo: detail?.id,
        originAddress: `${detail?.loadPlaceStAddress}, ${detail?.loadPlaceCity}`,
        destination: `${detail?.unloadPlaceCity}, ${detail?.unloadPlaceCountry}`,
      }));
    });
  };

  const getSurvey = () => {
    getSingleSurvey(surveyId).then((response) => {
      if (response?.data?.data?.status === "submitted") {
        getSurveyPreview();
      } else {
        const data = response?.data?.data;
        setSurveyData(data);
        setFormContent((prev) => ({
          ...prev,
          customerName: data?.name,
          customerEmail: data?.email,
          refNo: data?.id,
          originAddress: `${data?.loadPlaceStAddress}, ${data?.loadPlaceCity}`,
          destination: `${data?.unloadPlaceCity}, ${data?.unloadPlaceCountry}`,
        }));
      }
    });
  };

  const addContent = () => {
    setIsLoading(true);
    addFormContent({ formId: id, surveyId, content: formContent, mediaKeys: mediaKeysToSend })
      .then(() => {
        setSnackbarMessage("Weight Ticket added successfully!");
        setOpenSnackbar(true);
        setTimeout(() => navigate(-1), 1000);
      })
      .finally(() => setIsLoading(false));
  };

  const updateContent = () => {
    setIsLoading(true);
    updateFormContent({ formId: id, surveyId, content: formContent, mediaKeys: mediaKeysToSend, removedMediaKeys: mediaKeysToRemove }, formContentId)
      .then(() => {
        setSnackbarMessage("Weight Ticket updated successfully!");
        setOpenSnackbar(true);
        setIsEdit(false);
        getContent();
      })
      .finally(() => setIsLoading(false));
  };

  const getContent = () => {
    setIsLoading(true);
    getFormContent(id, surveyId).then((res) => {
      setFormContentId(res.data.data.uuid);
      setFormContent(res.data.data.content);
    }).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (flg === "1") getSurvey();
    else getContent();
  }, [flg]);

  const uploadFile = async (file) => {
    setIsLoading(true);
    setOpen(true);
    const fd = new FormData();
    fd.append("image", file);
    imageUpload(fd).then((res) => {
      const key = res?.data?.data?.key;
      setFormContent((prev) => {
        if (prev.adminSign) setMediaKeysToRemove((vals) => [...vals, prev.adminSign]);
        return { ...prev, adminSign: key };
      });
      setMediaKeysToSend((prev) => [...prev, key]);
    }).finally(() => {
      setIsLoading(false);
      setOpen(false);
    });
  };

  const sendEditRequestToAdmin = () => {
    setIsEditLoading(true);
    sendSuperAdminVerificationForEdit()
      .then(() => setIsVerified(true))
      .finally(() => setIsEditLoading(false));
  };

  let totals = { pieces: 0, gross: 0, net: 0, vol: 0, cft: 0, cbm: 0 };
  formContent.dataRows.forEach(row => {
    totals.pieces += Number(row.noOfPieces || 0);
    totals.gross += Number(row.grossWeight || 0);
    totals.net += Number(row.netWeight || 0);
    totals.vol += Number(row.volWeight || 0);
    totals.cft += Number(row.cft || 0);
    totals.cbm += Number(row.cbm || 0);
  });

  const pageActions = (
    <>
      {!isEdit && (
        <Button
          variant="contained"
          onClick={() => sendEditRequestToAdmin()}
          disabled={isEditLoading}
          startIcon={<NoteAltIcon />}
           sx={{ 
            textTransform: "none", 
            borderRadius: "var(--radius-md)", 
            padding: "10px 24px",
            fontWeight: 700,
            backgroundColor: "var(--primary-color)",
            boxShadow: "0 4px 12px rgba(79, 70, 229, 0.2)",
            '&:hover': { backgroundColor: 'var(--primary-hover)' }
          }}
        >
          Edit
        </Button>
      )}

      {isEdit && flg === "2" && (
        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            getContent();
            setIsEdit(false);
          }}
          disabled={isLoading}
           sx={{ textTransform: "none", borderRadius: "var(--radius-md)", fontWeight: 700, padding: "10px 24px", border: "1px solid" }}
        >
          Cancel
        </Button>
      )}

      {isEdit && (
        <Button
          variant="contained"
          onClick={() => {
            if (flg === "1") addContent();
            else updateContent();
          }}
          disabled={isLoading}
           sx={{ 
            textTransform: "none", 
            borderRadius: "var(--radius-md)", 
            padding: "10px 32px",
            fontWeight: 700,
            backgroundColor: "var(--primary-color)",
            boxShadow: "0 4px 12px rgba(79, 70, 229, 0.3)",
            '&:hover': { backgroundColor: 'var(--primary-hover)' }
          }}
        >
          Save
        </Button>
      )}

      {!isEdit && (
        <Button
          variant="contained"
          onClick={() => {
            if (window.innerWidth <= 768) {
      if (typeof setSnackbarMessage !== "undefined" && typeof setOpenSnackbar !== "undefined") {
        setSnackbarMessage("For a good experience, please use a desktop screen");
        setOpenSnackbar(true);
      } else {
        alert("For a good experience, please use a desktop screen");
      }
      return;
    }
            var tempTitle = document.title;
            document.title = `Weight Ticket-${
              formContent?.customerName
            }-${moment(new Date()).format("DD.MM.YYYY")}`;
            window.print();
            document.title = tempTitle;
          }}
          disabled={isLoading}
          startIcon={<DescriptionIcon />}
           sx={{ 
            textTransform: "none", 
            borderRadius: "var(--radius-md)", 
            padding: "10px 24px",
            fontWeight: 700,
            backgroundColor: "var(--text-main)",
             boxShadow: "0 4px 12px rgba(15, 23, 42, 0.2)",
             '&:hover': { backgroundColor: '#000' }
          }}
        >
          Save as PDF
        </Button>
      )}
    </>
  );

  return (
    <>
    <PremiumPageLayout title="Weight Ticket" actions={pageActions} onBack={() => navigate(-1)}>
      <div className="no-print">
        <SectionHeader title="Shipment Details" icon={<DescriptionIcon />} />
        <PremiumCard sx={{ mb: 4, p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ContactDetailField label="Ref No" isEdit={false} value={formContent.refNo} />
              <ContactDetailField
                label="Date"
                isEdit={isEdit}
                type="date"
                value={formContent.date}
                onChange={(e) => setFormContent({ ...formContent, date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ContactDetailField
                label="Origin"
                isEdit={isEdit}
                value={formContent.originAddress}
                onChange={(e) => setFormContent({ ...formContent, originAddress: e.target.value })}
              />
              <ContactDetailField
                label="Destination"
                isEdit={isEdit}
                value={formContent.destination}
                onChange={(e) => setFormContent({ ...formContent, destination: e.target.value })}
              />
            </Grid>
          </Grid>
        </PremiumCard>

        <SectionHeader title="Measuring & Weights" icon={<MonitorWeightIcon />} />
        <PremiumCard sx={{ mb: 4, overflow: "hidden" }}>
          <TableContainer component={Paper} elevation={0}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Pieces</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Gross (KG)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Net (KG)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Vol (KG)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>L (in)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>W (in)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>H (in)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>CFT / CBM</TableCell>
                  {isEdit && <TableCell sx={{ backgroundColor: '#f8fafc' }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {formContent.dataRows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {isEdit ? <Input type="number" value={row.noOfPieces} onChange={(e) => {
                        const newRows = [...formContent.dataRows];
                        newRows[index].noOfPieces = e.target.value;
                        setFormContent({ ...formContent, dataRows: newRows });
                      }} /> : (row.noOfPieces || "-")}
                    </TableCell>
                    <TableCell>
                      {isEdit ? <Input type="number" value={row.grossWeight} onChange={(e) => {
                        const newRows = [...formContent.dataRows];
                        newRows[index].grossWeight = e.target.value;
                        setFormContent({ ...formContent, dataRows: newRows });
                      }} /> : (row.grossWeight || "-")}
                    </TableCell>
                    <TableCell>
                      {isEdit ? <Input type="number" value={row.netWeight} onChange={(e) => {
                        const newRows = [...formContent.dataRows];
                        newRows[index].netWeight = e.target.value;
                        setFormContent({ ...formContent, dataRows: newRows });
                      }} /> : (row.netWeight || "-")}
                    </TableCell>
                    <TableCell>
                      {isEdit ? <Input type="number" value={row.volWeight} onChange={(e) => {
                        const newRows = [...formContent.dataRows];
                        newRows[index].volWeight = e.target.value;
                        setFormContent({ ...formContent, dataRows: newRows });
                      }} /> : (row.volWeight || "-")}
                    </TableCell>
                    <TableCell>
                      {isEdit ? <Input type="number" value={row.length} onChange={(e) => {
                        const newRows = [...formContent.dataRows];
                        newRows[index].length = e.target.value;
                        const cft = (Number(e.target.value) * Number(row.width) * Number(row.height)) / 1728;
                        newRows[index].cft = cft;
                        newRows[index].cbm = cft / 35;
                        setFormContent({ ...formContent, dataRows: newRows });
                      }} /> : (row.length || "-")}
                    </TableCell>
                    <TableCell>
                      {isEdit ? <Input type="number" value={row.width} onChange={(e) => {
                        const newRows = [...formContent.dataRows];
                        newRows[index].width = e.target.value;
                        const cft = (Number(row.length) * Number(e.target.value) * Number(row.height)) / 1728;
                        newRows[index].cft = cft;
                        newRows[index].cbm = cft / 35;
                        setFormContent({ ...formContent, dataRows: newRows });
                      }} /> : (row.width || "-")}
                    </TableCell>
                    <TableCell>
                      {isEdit ? <Input type="number" value={row.height} onChange={(e) => {
                        const newRows = [...formContent.dataRows];
                        newRows[index].height = e.target.value;
                        const cft = (Number(row.length) * Number(row.width) * Number(e.target.value)) / 1728;
                        newRows[index].cft = cft;
                        newRows[index].cbm = cft / 35;
                        setFormContent({ ...formContent, dataRows: newRows });
                      }} /> : (row.height || "-")}
                    </TableCell>
                    <TableCell>{row.cft ? `${row.cft.toFixed(2)} / ${row.cbm.toFixed(2)}` : "-"}</TableCell>
                    {isEdit && (
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => handleRemoveDataRows(index)}>
                          <img src={trashIcn} alt="delete" height={18} width={18} />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {isEdit && (
            <Box sx={{ p: 2, textAlign: 'right' }}>
              <Button onClick={handleAddNewDataRows}>Add Row</Button>
            </Box>
          )}
          <Box sx={{ p: 2, bgcolor: '#f1f5f9', display: 'flex', gap: 3, fontWeight: 'bold' }}>
            <span>Total Pieces: {totals.pieces}</span>
            <span>Total Gross: {totals.gross.toFixed(2)} KG</span>
            <span>Total CFT / CBM: {totals.cft.toFixed(2)} / {totals.cbm.toFixed(2)}</span>
          </Box>
        </PremiumCard>

        <SectionHeader title="Authorized Signature" icon={<GavelIcon />} />
        <PremiumCard sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ height: 100, borderBottom: '1px solid #cbd5e1', mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {formContent.adminSign ? <img src={Bucket + formContent.adminSign} alt="signature" height={80} /> : isEdit && <Button variant="outlined" onClick={() => setSignCanvas(true)}>Upload Signature</Button>}
                </Box>
                <Typography variant="body2" fontWeight="bold">Signature of Authorized</Typography>
              </Box>
            </Grid>
          </Grid>
        </PremiumCard>
      </div>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Paper elevation={6} sx={{ p: 2, bgcolor: snackbarMessage.includes("success") ? 'success.main' : 'error.main', color: 'white' }}>
          {snackbarMessage}
        </Paper>
      </Snackbar>

      <LoadingModal open={open} handleClose={() => setOpen(false)} />
      {isSignCanvas && <MySignatureCanvas open={isSignCanvas} onClose={() => setSignCanvas(false)} onSuccess={uploadFile} />}
      {isVerified && <SuperAdminVerifationModal open={isVerified} onClose={() => setIsVerified(false)} onSuccess={() => { setIsEdit(true); setIsVerified(false); }} onError={() => { setSnackbarMessage("Invalid Pin"); setOpenSnackbar(true); }} />}
      </PremiumPageLayout>

      <PrintComponent>
        <WeightTicketDownload
          surveyData={surveyData}
          formContent={formContent}
        />
      </PrintComponent>
    </>
  );
};
export default WeightTicketFormPage;
